import React from "react";
import { Heading, Paragraph, Table } from "@digdir/designsystemet-react";
import Link from "@/components/link";

type Props = {
    tagName: string;
    attributes: Record<string, string>;
    children: React.ReactNode;
};

export function XhtmlComponentRenderer({
    tagName,
    attributes,
    children,
}: Props) {
    const key = attributes.id || `${tagName}-${Math.random()}`;

    const tableTags = {
        thead: (t: React.ComponentProps<typeof Table.Head>) => (
            <Table.Head {...t} />
        ),
        th: (t: React.ComponentProps<typeof Table.HeaderCell>) => (
            <Table.HeaderCell {...t} />
        ),
        tbody: (t: React.ComponentProps<typeof Table.Body>) => (
            <Table.Body {...t} />
        ),
        td: (t: React.ComponentProps<typeof Table.Cell>) => <Table.Cell {...t} />,
        tr: (t: React.ComponentProps<typeof Table.Row>) => <Table.Row {...t} />,
    } as const;

    switch (tagName) {
        case "h1":
            return (
                <Heading data-size="xl" level={1} key={key} {...attributes}>
                    {children}
                </Heading>
            );
        case "h2":
            return (
                <Heading data-size="lg" level={2} key={key} {...attributes}>
                    {children}
                </Heading>
            );
        case "h3":
            return (
                <Heading data-size="md" level={3} key={key} {...attributes}>
                    {children}
                </Heading>
            );
        case "h4":
            return (
                <Heading data-size="sm" level={4} key={key} {...attributes}>
                    {children}
                </Heading>
            );
        case "h5":
            return (
                <Heading data-size="xs" level={5} key={key} {...attributes}>
                    {children}
                </Heading>
            );
        case "h6":
            return (
                <Heading data-size="2xs" level={6} key={key} {...attributes}>
                    {children}
                </Heading>
            );
        case "p":
            return (
                <Paragraph className="text-md" variant="long" key={key} {...attributes}>
                    {children}
                </Paragraph>
            );

        case "a":
            return (
                <Link key={key} className="text-md" {...attributes}>
                    {children}
                </Link>
            );
        case "br":
            return <br key={key} {...attributes} />;
        case "img":
            return <img key={key} {...attributes} alt={attributes.alt ?? " "} />;
        case "pre":
            return (
                <pre key={key} {...attributes}>
                    {children}
                </pre>
            );

        case "table":
            return (
                <div className={"bodytext"} >
                    <Table data-color="neutral" key={key} {...attributes}>
                        {children}
                    </Table>
                </div>
            );
        case "thead":
        case "tbody":
        case "tr":
        case "td":
        case "th": {
            const TableComponent = tableTags[tagName];
            if (TableComponent) {
                return (
                    <TableComponent key={key} {...attributes}>
                        {children}
                    </TableComponent>
                );
            }
            break;
        }

        case "b":
        case "strong":
        case "i":
        case "em":
        case "ul":
        case "ol":
        case "li":
        case "div":
        case "span":
            return React.createElement(tagName, { ...attributes, key }, children);

        default:
            console.warn(`Ukjent XHTML-tag: <${tagName}> - fallback til <span>`);
            return (
                <span key={key} {...attributes}>
                    {children}
                </span>
            );
    }
}
